import { format, parseISO } from 'date-fns';
import { LuArrowUpRight } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { formatCurrency } from 'utils';
import { useListAccountPlannedWithdrawals } from 'views/portfolio/account-group-queries';

import { getWithdrawalPlanRange, summarizeWithdrawalPlan } from './withdrawal-plan';

interface WithdrawalPlanCardProps {
    accountGUID: string;
    accountGroupGUID: string;
    planningPath: string;
}

export const WithdrawalPlanCard = ({ accountGUID, accountGroupGUID, planningPath }: WithdrawalPlanCardProps) => {
    const today = new Date();
    const year = today.getFullYear();
    const { startMonth, endMonth } = getWithdrawalPlanRange(false, today);
    const { data, isPending, isError } = useListAccountPlannedWithdrawals(
        accountGroupGUID,
        accountGUID,
        { startMonth, endMonth },
        { throwOnError: false },
    );
    const { bars, count, paidCount, total, paid } = summarizeWithdrawalPlan(data ?? [], false);
    const maxAmount = Math.max(...bars.map((bar) => bar.total), 1);
    const monthLabel = (month: string, pattern: string) => format(parseISO(`${month}-01`), pattern);
    const unavailable = isPending || isError;

    return (
        <Card className="border-border flex min-h-[32rem] flex-col rounded-2xl bg-white p-4 lg:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h2 className="font-heading text-cocoa-1000 text-xl">Withdrawal plan</h2>
                    <p className="text-cocoa-500 mt-1 text-sm">
                        {monthLabel(startMonth, 'MMM')} – {monthLabel(endMonth, 'MMM yyyy')}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-cocoa-1000 rounded-lg bg-stone-100 px-4 py-2 text-sm font-medium">
                        {year}
                    </span>
                    <Button asChild variant="outline" size="icon" className="shrink-0 rounded-lg">
                        <Link to={planningPath} aria-label="View liquidity planning">
                            <LuArrowUpRight className="h-5 w-5" />
                        </Link>
                    </Button>
                </div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4">
                <div>
                    <div className="text-cocoa-500 text-[10px] font-medium tracking-wider uppercase">
                        {year} withdrawals
                    </div>
                    <div className="text-cocoa-1000 mt-3 text-2xl font-medium tabular-nums">
                        {unavailable ? '—' : formatCurrency(total)}
                    </div>
                    <div className="text-cocoa-500 mt-2 text-sm">
                        {unavailable ? '—' : `${count} ${count === 1 ? 'withdrawal' : 'withdrawals'}`}
                    </div>
                </div>
                <div>
                    <div className="text-cocoa-500 text-[10px] font-medium tracking-wider uppercase">Paid to date</div>
                    <div className="text-cocoa-1000 mt-3 text-2xl font-medium tabular-nums">
                        {unavailable ? '—' : formatCurrency(paid)}
                    </div>
                    <div className="text-cocoa-500 mt-2 text-sm">{unavailable ? '—' : `${paidCount} of ${count}`}</div>
                </div>
            </div>
            {unavailable || count === 0 ? (
                <div
                    className="text-cocoa-400 mt-6 flex min-h-52 flex-1 items-center justify-center text-sm"
                    role="status"
                >
                    {isPending
                        ? 'Loading withdrawal plan…'
                        : isError
                          ? 'Unable to load withdrawal plan'
                          : 'No withdrawals planned for this period'}
                </div>
            ) : (
                <div
                    className="border-border mt-6 flex min-h-52 flex-1 items-end gap-1.5 border-b"
                    role="list"
                    aria-label="Monthly withdrawal amounts"
                >
                    {bars.map((bar) => {
                        const description = `${monthLabel(bar.yearMonth, 'MMMM yyyy')}: ${formatCurrency(bar.paid)} paid, ${formatCurrency(bar.pending)} upcoming`;
                        return (
                            <div key={bar.yearMonth} className="flex h-52 min-w-0 flex-1 items-end" role="listitem">
                                <div
                                    className="flex w-full flex-col justify-end overflow-hidden rounded-t-md"
                                    style={{ height: `${(bar.total / maxAmount) * 100}%` }}
                                    title={description}
                                    aria-label={description}
                                >
                                    <div
                                        className="bg-[#bad8dc]"
                                        style={{ height: `${bar.total ? (bar.pending / bar.total) * 100 : 0}%` }}
                                    />
                                    <div
                                        className="bg-[#47838d]"
                                        style={{ height: `${bar.total ? (bar.paid / bar.total) * 100 : 0}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <div className="text-cocoa-500 mt-4 flex flex-wrap items-center justify-between gap-3 text-xs">
                <span>{monthLabel(startMonth, 'MMM ’yy')}</span>
                <div className="flex items-center gap-4">
                    <span className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-sm bg-[#47838d]" />
                        Paid
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-sm bg-[#bad8dc]" />
                        Upcoming
                    </span>
                </div>
                <span>{monthLabel(endMonth, 'MMM ’yy')}</span>
            </div>
        </Card>
    );
};
